/**
 * <SchoolAssessmentRow> — le prochain contrôle, sur une ligne (molecule).
 *
 * Le picto de la matière, l'intitulé « Prochain contrôle », la matière et le titre du contrôle, sa
 * date et sa chip J-n ; à droite, le pourcentage de préparation **quand il existe** — un contrôle
 * dont aucun chapitre n'est renseigné n'a pas de préparation à annoncer, et « 0 % » se lirait
 * comme un reproche.
 *
 * Toute la ligne ouvre le détail du contrôle (chapitres, pièces jointes, modifier, supprimer) :
 * l'accordéon a quitté l'accueil, pas ce qu'il permettait.
 *
 * **Elle ne dessine pas de carte** : le bloc « Suivi » est UNE carte-liste. Plus de liseré
 * d'urgence à gauche non plus — l'urgence se lit sur la chip J-n, qui la porte déjà en couleur.
 *
 * `dimmed` : le contrôle s'en va (sa suppression est partie). Le picto s'estompe, le titre passe à
 * l'encre secondaire, le pourcentage et le chevron se retirent, et la ligne ne répond plus.
 */
import React from "react"
import { Pressable } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Badge } from "../../atoms/Badge"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Icon } from "../../atoms/icons/Icon"
import { ImageIcon } from "../../atoms/icons/ImageIcon"
import { Text } from "../../atoms/typography/Text"
import { CardListRow } from "../CardList"
import { URGENCY_VARIANT } from "../SchoolAssessmentCard"
import { type UISchoolAssessment } from "../../../ports/primary/ui/types"
import { daysUntil, formatDayMonth, formatDaysUntil } from "../../../ports/primary/ui/formatters/dateFormatter"
import { preparationProgress } from "../../../ports/primary/ui/formatters/schoolAssessmentFormatter"

/** Le picto de la matière — exporté pour la place que son squelette lui garde. */
export const SUBJECT_ICON_SIZE = 28
/** Le picto d'une ligne dense (`dense`). */
const DENSE_ICON_SIZE = 20

export type SchoolAssessmentRowProps = {
    assessment: UISchoolAssessment
    /** Libellé de tête — « Prochain contrôle » par défaut. */
    eyebrow?: string
    /** Ouvre le détail du contrôle — toute la ligne est la cible. */
    onPress?: () => void
    /** Le contrôle s'en va : la ligne s'estompe et ne répond plus. Défaut `false`. */
    dimmed?: boolean
    /**
     * Une colonne étroite (la pellicule du live du masteur, 190 px) : le picto plus petit, sans la
     * préparation en %, l'intitulé en petit. Défaut `false`.
     */
    dense?: boolean
    testID?: string
}

export function SchoolAssessmentRow(props: SchoolAssessmentRowProps) {
    const { assessment, eyebrow = "Prochain contrôle", onPress, dimmed = false, dense = false, testID } = props
    const { title, subject, date, chaptersProgress, urgency } = assessment

    const days = daysUntil(date)
    const daysLabel = formatDaysUntil(days)
    const hasProgress = chaptersProgress.length > 0 && !dimmed && !dense
    const pressable = onPress != null && !dimmed
    const progress = preparationProgress(chaptersProgress)

    // « Contrôle Mathématiques » porte déjà la matière : la réécrire juste dessous la dit deux
    // fois de suite, sous un picto qui la dit une troisième. On ne la garde que si le titre est
    // muet dessus (« Chapitre 3 et 4 »).
    const dateLabel = formatDayMonth(date)
    const titleSaysSubject = title.toLowerCase().includes(subject.name.toLowerCase())
    const meta = titleSaysSubject ? dateLabel : `${subject.name} · ${dateLabel}`

    return (
        <Pressable testID={testID} onPress={onPress} disabled={!pressable} accessibilityRole="button">
            <CardListRow>
                <Row gap={dense ? "2" : "3"} align="center">
                    <ImageIcon
                        uri={subject.iconUrl}
                        size={dense ? DENSE_ICON_SIZE : SUBJECT_ICON_SIZE}
                        dimmed={dimmed}
                    />

                    <Stack gap={dense ? "0" : "1"} style={styles.body}>
                        <Text size={dense ? "text-xs" : "text-sm"} color="text-muted">
                            {eyebrow}
                        </Text>
                        <Text
                            size={dense ? "text-sm" : "text-md"}
                            weight="bold"
                            color={dimmed ? "text-muted" : undefined}
                            numberOfLines={1}
                        >
                            {title}
                        </Text>
                        <Row gap="2" align="center" style={styles.meta}>
                            <Text size="text-sm" color="text-muted">
                                {meta}
                            </Text>
                            <Badge text={daysLabel} variant={URGENCY_VARIANT[urgency]} tinted />
                        </Row>
                    </Stack>

                    {hasProgress && (
                        <Text size="text-md" weight="bold" color="text-muted">
                            {`${progress} %`}
                        </Text>
                    )}
                    {pressable && <Icon name="chevron-right" size={16} color="text-muted" />}
                </Row>
            </CardListRow>
        </Pressable>
    )
}

const styles = StyleSheet.create(() => ({
    body: {
        flex: 1,
        flexShrink: 1,
    },
    meta: {
        flexWrap: "wrap",
    },
}))
